import { useTranslate } from '@tolgee/react';
import { Tooltip } from 'antd';
import React from 'react';
import styled, { css } from 'styled-components';

import {
  AIFeedbackFilledSvg,
  AnswersFilledSvg,
  DrawingFilledSvg,
  PollFilledSvg,
  WordCloudFilledSvg,
} from '../../../assets/icons/ActivityIcons';
import GridSvg from '../../../assets/icons/GridSvg';
import IdeasSvg from '../../../assets/icons/IdeasSvg';
import PodiumSvg from '../../../assets/icons/PodiumSvg';
import SlideHeaderSvg from '../../../assets/icons/SlideHeaderSvg';
import { SlideHeaderText } from '../../../assets/icons/SlideHeaderText';
import { SlideImageTextSvg } from '../../../assets/icons/SlideImageTextSvg';
import { SlideTextImageSvg } from '../../../assets/icons/SlideTextImageSvg';
import TitleImageSvg from '../../../assets/icons/TitleImageSvg';
import VotingSvg from '../../../assets/icons/VotingSvg';
import YoutubeSvg from '../../../assets/icons/YoutubeSvg';
import theme from '../../../assets/theme';
import { SlideType as SlideContentType } from '../../../services/backendService/types';
import { zIndexes } from '../../../utils/zIndexes';
import { Button } from '../../_atoms/Button';
import ConditionalWrapper from '../../_atoms/ConditionalWrapper';
import { Slide, SlideType } from '../../SlideComponents/slide.types';

export const RoundTypeButton = ({
  round,
  button,
  handleRoundTypeChanged,
  onHover,
  onHoverLeave,
}: {
  handleRoundTypeChanged: (type: SlideType) => void;
  round?: Slide;
  button: QuestionButtonsType;
  onHover?: () => void;
  onHoverLeave?: () => void;
}) => {
  const { t } = useTranslate();
  const isDisabled =
    button.disabled ||
    !!(
      button.disabledForRounds &&
      round?.type &&
      button.disabledForRounds.includes(round.type)
    );

  const tooltipText = useActivitySelectorTooltip(button.type as SlideType);

  return (
    <ConditionalWrapper
      key={button.text}
      condition={isDisabled}
      truthyWrapper={(children: React.ReactNode) => (
        <Tooltip
          title={
            button.hoverText
              ? button.hoverText
              : button.disabled
              ? t('Coming soon!', 'Coming soon!')
              : ''
          }
        >
          <DisabledButtonContainer>{children}</DisabledButtonContainer>
        </Tooltip>
      )}
    >
      <Tooltip title={tooltipText} mouseEnterDelay={1}>
        <TypeButton
          key={button.text}
          $disabled={isDisabled}
          onMouseEnter={onHover}
          onMouseLeave={onHoverLeave}
          onClick={() => !isDisabled && handleRoundTypeChanged(button.type as SlideType)}
        >
          {button.beta && <BetaFlag />}
          <ButtonIconWrapper>{button.icon}</ButtonIconWrapper>
          <p>{button.text}</p>
        </TypeButton>
      </Tooltip>
    </ConditionalWrapper>
  );
};

export const ButtonIconWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 30px;
  height: 30px;
  & span.anticon {
    width: 30px;
    height: 30px;
  }
  svg {
    height: 30px;
    width: 30px;
    margin: 0;
  }
`;

export const BetaFlag = ({
  dropdown,
  showRightSide,
  top,
  status = 'Beta',
  tagColor = theme.colors.orange,
}: {
  dropdown?: boolean;
  showRightSide?: boolean;
  top?: number;
  status?: string;
  tagColor?: string;
}) => {
  return (
    <BetaContainer $top={top} $isDropdown={dropdown}>
      <Flag
        $isDropdown={dropdown}
        $showRightSide={showRightSide}
        $backgroundColor={tagColor}
      >
        <span>{status}</span>
      </Flag>
    </BetaContainer>
  );
};

const Flag = styled.div<{
  $isDropdown?: boolean;
  $showRightSide?: boolean;
  $backgroundColor?: string;
}>`
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
  border-top-right-radius: ${({ $showRightSide }) => ($showRightSide ? '10px' : '0px')};
  border-bottom-right-radius: ${({ $showRightSide }) =>
    $showRightSide ? '10px' : '0px'};
  background: ${({ $backgroundColor }) => $backgroundColor || theme.colors.orange};
  padding: ${({ $showRightSide }) =>
    $showRightSide ? ' 3px 8px 3px 8px' : ' 3px 5px 3px 8px;'};
  box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  span {
    color: ${theme.colors.black} !important;
    font-weight: bold;
    font-size: 10px;
    line-height: 10px;

    margin-bottom: 0px;
  }

  ${({ $isDropdown }) =>
    $isDropdown &&
    css`
      border-radius: 20px 0px 0px 20px;

      span {
        font-size: 12px;
        padding: 4px 12px 4px 7px;
      }
    `}
`;

const BetaContainer = styled.div<{ $isDropdown?: boolean; $top?: number }>`
  position: absolute;
  top: ${({ $top }) => ($top ? $top : 12)}px;
  right: 0px;
  z-index: ${zIndexes('CREATOR_ROUND_SELECTOR_BETA_LABEL')};

  ${({ $isDropdown }) =>
    $isDropdown &&
    css`
      top: 13px;
    `}
`;

export const TypeButton = styled(Button)<{ $disabled: boolean; $active?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 100px;
  padding: 0px;
  height: 70px;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.black};
  color: ${theme.colors.black};

  &:hover {
    background-color: rgba(165, 165, 165, 0.1);
    background-color: ${theme.colors.green};
    color: ${theme.colors.white};
  }
  p {
    font-size: 14px;
    margin-top: 5px;
    white-space: normal;
    line-height: 14px;
    color: inherit;
    margin-bottom: 0px;
    font-weight: bold;
    width: 100%;
  }

  ${({ $active }) =>
    $active &&
    css`
      background-color: rgba(165, 165, 165, 0.1);
      background-color: ${theme.colors.green};
      color: ${theme.colors.white};
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      border: 1px solid #a5a5a5 !important;
      color: #a5a5a5;
      background: ${theme.colors.white} !important;
      p {
        color: #a5a5a5;
      }
      svg {
        color: #a5a5a5;
      }
    `}
`;

function useActivitySelectorTooltip(roundType?: SlideType | null) {
  const { t } = useTranslate();
  if (roundType === 'answers')
    return t(
      'Participants answer an open ended question',
      'Participants answer an open ended question',
    );
  if (roundType === 'aiAssessedAnswers')
    return t(
      'Participants answer an open ended question and get feedback',
      'Participants answer an open ended question and get feedback',
    );
  if (roundType === 'canvas')
    return t(
      'Drawings created by the participants',
      'Drawings created by the participants',
    );
  if (roundType === 'poll')
    return t(
      'Participants vote on different options which creates a poll',
      'Participants vote on different options which creates a poll',
    );
  if (roundType === 'wordcloud')
    return t(
      'Participants sends in words and their collection creates a wordcloud',
      'Participants sends in words and their collection creates a wordcloud',
    );
  return '';
}

const DisabledButtonContainer = styled.div`
  button {
    width: 100%;
  }
`;

export type QuestionButtonsType = {
  type: SlideType | SlideContentType | null;
  text: string;
  icon: React.ReactNode;
  responseCategory: string;
  disabled: boolean;
  beta?: boolean;
  disabledForRounds?: string[];
  hoverText?: string;
};

export function useRoundTypeButtons(): {
  slide: QuestionButtonsType[];
  response: QuestionButtonsType[];
  collaboration: QuestionButtonsType[];
  results: QuestionButtonsType[];
} {
  const { t } = useTranslate();
  return {
    slide: [
      {
        type: 'header',
        text: t('Header', 'Header'),
        icon: <SlideHeaderSvg />,
        responseCategory: 'slide',
        disabled: false,
      },
      {
        type: 'image',
        text: t('Title & media'),
        icon: <TitleImageSvg />,
        responseCategory: 'slide',
        disabled: false,
      },
      {
        type: 'header_text',
        text: t('Text', 'Text'),
        icon: <SlideHeaderText />,
        responseCategory: 'slide',
        disabled: false,
      },
      {
        type: 'text_image',
        text: t('Text & media', 'Text & media'),
        icon: <SlideTextImageSvg />,
        responseCategory: 'slide',
        disabled: false,
      },
      {
        type: 'image_text',
        text: t('Media & text', 'Media & text'),
        icon: <SlideImageTextSvg />,
        responseCategory: 'slide',
        disabled: false,
      },
      {
        type: 'fullscreen_media',
        text: t('Fullscreen media'),
        icon: <YoutubeSvg />,
        responseCategory: 'slide',
        disabled: false,
      },
    ],
    response: [
      {
        type: 'poll',
        text: t('Poll', 'Poll'),
        icon: <PollFilledSvg />,
        responseCategory: 'popular',
        disabled: false,
        beta: false,
      },
      {
        type: 'wordcloud',
        text: t('Word cloud', 'Word cloud'),
        icon: <WordCloudFilledSvg />,
        responseCategory: 'popular',
        disabled: false,
        beta: false,
      },
      {
        type: 'answers',
        text: t('Open question', 'Open question'),
        icon: <AnswersFilledSvg />,
        responseCategory: 'popular',
        disabled: false,
      },
      {
        type: 'aiAssessedAnswers',
        text: t('AI Feedback', 'AI Feedback'),
        icon: <AIFeedbackFilledSvg />,
        responseCategory: 'popular',
        disabled: false,
        beta: false,
      },
      {
        type: 'canvas',
        text: t('Drawings', 'Drawings'),
        icon: <DrawingFilledSvg />,
        responseCategory: 'popular',
        disabled: false,
        beta: false,
      },
    ],
    collaboration: [
      {
        type: 'voting',
        text: t('Voting', 'Voting'),
        icon: <VotingSvg />,
        responseCategory: 'collaboration',
        disabled: false,
      },
      {
        type: null,
        text: t('Group voting', 'Group voting'),
        icon: <IdeasSvg />,
        responseCategory: 'collaboration',
        disabled: true,
      },
    ],
    results: [
      {
        type: 'podium',
        text: t('Top 5', 'Top 5'),
        icon: <PodiumSvg />,
        responseCategory: 'results',
        disabled: false,
        disabledForRounds: ['responsegrid'],
      },
      {
        type: 'responsegrid',
        text: t('Grid', 'Grid'),
        icon: <GridSvg />,
        responseCategory: 'results',
        disabled: false,
        beta: true,
        disabledForRounds: ['podium'],
        hoverText: t(
          'Disable voting to use grid display',
          'Disable voting to use grid display',
        ),
      },
    ],
  };
}

export const BackTitleGrid = styled.span`
  display: grid;
  grid-template-columns: 70px auto 70px;
  align-items: center;
  width: 100%;
  h1 {
    font-size: 24px !important;
    text-align: center;
  }

  @media (max-width: 900px) {
    grid-template-columns: 20px auto 20px;
    h1 {
      font-size: 16px !important;
    }
  }
  margin-bottom: 10px;
`;
